import { useEffect, useMemo, useRef, useState } from "react";
import { MultiSelectControlledOption } from "components";
import { calculateCurrentFiscalYear, getFiscalYear } from "utils";
import { LOCAL_STORAGE_KEYS } from "consts";
import { ALL_STATUS_OPTIONS, INITIAL_STATUS_OPTIONS } from "../consts";
import {
  clearStoredFilters,
  findOrBuildOption,
  getStatusOptions,
  readAndParseStoredFilters,
} from "../helpers";
import { DefaultObjectProps } from "../types";
import { UseMissingDocumentsFiltersProps } from "./types";

export const useMissingDocumentsFilters = ({
  sites,
  companyMonths,
  hasCompanyFiscalConfig,
  sitesByCountry,
  countries,
  utilityTypes,
  businessUnits,
  countryRegions,
}: UseMissingDocumentsFiltersProps) => {
  const {
    storedFiscalYear,
    hasStoredState,
    parsedCountry,
    parsedSiteIds,
    parsedUtilityType,
    parsedBusinessUnit,
    parsedCountryRegion,
    parsedStatus,
  } = useMemo(() => readAndParseStoredFilters(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const initialFY =
    storedFiscalYear ?? calculateCurrentFiscalYear(companyMonths);
  const selectedFiscalYear = getFiscalYear(companyMonths, initialFY);

  const defaultObject = useRef<DefaultObjectProps>({
    siteIds: sites.map((site: any) => site.value),
    utilityTypeIds: null,
    fromDate: selectedFiscalYear.fromDate,
    toDate: selectedFiscalYear.endDate,
    businessUnits: null,
    countryRegions: null,
    countries: null,
    onlyMissing: false,
    page: 0,
    size: 50,
    collectorStatuses: INITIAL_STATUS_OPTIONS.map((s) => s.key),
  });

  const [currentFY, setCurrentFY] = useState<string>(initialFY);
  const [onlyMissing, setOnlyMissingState] = useState(false);

  const setOnlyMissing = (value: boolean) => {
    setOnlyMissingState(value);
    defaultObject.current = { ...defaultObject.current, onlyMissing: value };
  };
  const [searchActive, setSearchActive] = useState(false);
  const [enableSearch, setEnableSearch] = useState(false);
  const [areSitesFiltered, setAreSitesFiltered] = useState(false);

  const [currentCountries, setCurrentCountries] = useState<
    MultiSelectControlledOption[]
  >(parsedCountry ?? []);
  const [currentSiteIds, setCurrentSiteIds] = useState<
    MultiSelectControlledOption[]
  >(parsedSiteIds ?? []);
  const [currentUtilityTypeIds, setCurrentUtilityTypeIds] = useState<
    MultiSelectControlledOption[]
  >(parsedUtilityType ?? []);
  const [currentBusinessUnits, setCurrentBusinessUnits] = useState<
    MultiSelectControlledOption[]
  >(parsedBusinessUnit ?? []);
  const [currentCountryRegions, setCurrentCountryRegions] = useState<
    MultiSelectControlledOption[]
  >(parsedCountryRegion ?? []);
  const [currentStatus, setCurrentStatus] = useState<
    MultiSelectControlledOption[]
  >(
    parsedStatus?.length
      ? parsedStatus
      : getStatusOptions(INITIAL_STATUS_OPTIONS),
  );

  // Initialize defaultObject once sites are available
  useEffect(() => {
    if (!hasStoredState) {
      const { fromDate, endDate } = getFiscalYear(
        companyMonths,
        currentFY || "",
      );
      defaultObject.current = {
        ...defaultObject.current,
        siteIds: sites.map((site: any) => site.value),
        utilityTypeIds: null,
        fromDate,
        toDate: endDate,
        businessUnits: null,
        countryRegions: null,
      };
    }
    setEnableSearch(true);
  }, [sites]); // eslint-disable-line

  // Sync FY when companyMonths loads and no stored FY
  useEffect(() => {
    if (!storedFiscalYear && companyMonths) {
      const fy = calculateCurrentFiscalYear(companyMonths);
      if (currentFY !== fy) setCurrentFY(fy);
    }
  }, [companyMonths]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep defaultObject date range in sync with currentFY and companyMonths
  useEffect(() => {
    if (!hasCompanyFiscalConfig) return;
    const { fromDate, endDate } = getFiscalYear(companyMonths, currentFY || "");
    defaultObject.current = {
      ...defaultObject.current,
      fromDate,
      toDate: endDate,
    };
  }, [companyMonths, currentFY, hasCompanyFiscalConfig]);

  // Sync defaultObject.current with stored filter values on mount
  useEffect(() => {
    const { fromDate, endDate } = getFiscalYear(
      companyMonths,
      storedFiscalYear ?? initialFY,
    );
    const siteIdsForApi = parsedSiteIds?.length
      ? parsedSiteIds.map((o) => o.value)
      : sites.map((site: any) => site.value);

    defaultObject.current = {
      ...defaultObject.current,
      siteIds: siteIdsForApi,
      utilityTypeIds: parsedUtilityType?.length
        ? parsedUtilityType.map((o) => o.value)
        : null,
      fromDate,
      toDate: endDate,
      businessUnits: parsedBusinessUnit?.length
        ? parsedBusinessUnit.map((o) => o.value)
        : null,
      countryRegions: parsedCountryRegion?.length
        ? parsedCountryRegion.map((o) => o.value)
        : null,
      countries: parsedCountry?.length
        ? parsedCountry.map((o) => o.value)
        : null,
      onlyMissing: false,
      page: 0,
      size: 50,
      collectorStatuses: parsedStatus?.length
        ? parsedStatus.map((o) => o.value)
        : INITIAL_STATUS_OPTIONS.map((s) => s.key),
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateArr = (
    arr: any[],
    value: string | number,
    unchecked?: boolean,
    isCountrySelection = false,
  ) => {
    const safeArr = Array.isArray(arr) ? arr : [];
    const currentSelection = Array.from(
      new Set([...safeArr.map((item: any) => item.value), value]),
    );
    if (isCountrySelection) {
      setAreSitesFiltered(true);
      if (unchecked) {
        const filteredSelection = safeArr
          .filter((item: any) => item.value !== value)
          .map((item: any) => item.value);
        if (!filteredSelection.length) {
          setAreSitesFiltered(false);
          return null;
        }
        return filteredSelection;
      }
      return currentSelection
        .flatMap((country: any) => sitesByCountry[country])
        .filter((site: any) => site !== undefined);
    }
    if (safeArr.find((item: any) => item.value === "ALL")) return null;
    if (unchecked) {
      const filteredSelection = safeArr
        .filter((item: any) => item.value !== value)
        .map((item: any) => item.value);
      if (!filteredSelection.length) return null;
      return filteredSelection;
    }
    return currentSelection.includes("ALL") ? null : currentSelection;
  };

  const getSitesFiltered = () => {
    const filteredSiteIds = new Set(
      currentCountries.flatMap(
        (country: any) => sitesByCountry[country.value] ?? [],
      ),
    );
    return sites.filter((site: any) => filteredSiteIds.has(site.value));
  };

  const createSetSelectedValues =
    (
      storageKey: string,
      setState: (param: MultiSelectControlledOption[]) => void,
      getDefaultObjectUpdate: (
        param: MultiSelectControlledOption[],
      ) => Partial<DefaultObjectProps>,
      options?: { setSearchActive?: boolean },
    ) =>
    (param: MultiSelectControlledOption[]) => {
      setState(param);
      localStorage.setItem(storageKey, JSON.stringify(param));
      const update = getDefaultObjectUpdate(param);
      if (Object.keys(update).length > 0) {
        defaultObject.current = { ...defaultObject.current, ...update };
      }
      if (options?.setSearchActive !== false) setSearchActive(true);
    };

  const createOnChangeFn =
    (config: {
      storageKey: string;
      setState: (param: MultiSelectControlledOption[]) => void;
      run: (
        value: string | number,
        unchecked: boolean | undefined,
      ) => {
        newParam: MultiSelectControlledOption[];
        defaultObjectUpdate: Partial<DefaultObjectProps>;
      };
      updateStateAndStorage?: boolean;
    }) =>
    (value: string | number, unchecked: boolean | undefined) => {
      const { newParam, defaultObjectUpdate } = config.run(value, unchecked);
      defaultObject.current = {
        ...defaultObject.current,
        ...defaultObjectUpdate,
      };
      if (config.updateStateAndStorage !== false) {
        config.setState(newParam);
        localStorage.setItem(config.storageKey, JSON.stringify(newParam));
      }
      setSearchActive(true);
    };

  const allStatusOptions = useMemo(
    () => getStatusOptions(ALL_STATUS_OPTIONS),
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── pre-built filter handlers ────────────────────────────────────────────

  const countrySetSelectedValues = createSetSelectedValues(
    LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COUNTRY,
    setCurrentCountries,
    (param) => ({
      siteIds:
        param.length > 0
          ? (param.flatMap((c) => sitesByCountry[c.value] ?? []) as number[])
          : sites.map((site: any) => site.value),
      countries: param.length > 0 ? param.map((c) => c.value) : null,
    }),
  );

  const countryOnChange = createOnChangeFn({
    storageKey: LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COUNTRY,
    setState: setCurrentCountries,
    run: (value, unchecked) => {
      const newSiteIds =
        updateArr(currentCountries, value, unchecked, true) ??
        sites.map((site: any) => site.value);
      const newParam = unchecked
        ? currentCountries.filter((c: any) => c.value !== value)
        : ([...currentCountries, findOrBuildOption(value, countries)].filter(
            Boolean,
          ) as MultiSelectControlledOption[]);
      return {
        newParam,
        defaultObjectUpdate: {
          siteIds: newSiteIds,
          countries:
            newParam.length > 0 ? newParam.map((c: any) => c.value) : null,
        },
      };
    },
  });

  const utilityTypeSetSelectedValues = createSetSelectedValues(
    LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_UTILITY_TYPE,
    setCurrentUtilityTypeIds,
    (param) => ({
      utilityTypeIds: param.length > 0 ? param.map((o) => o.value) : null,
    }),
  );

  const utilityTypeOnChange = createOnChangeFn({
    storageKey: LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_UTILITY_TYPE,
    setState: setCurrentUtilityTypeIds,
    run: (value, unchecked) => {
      const newVal = updateArr(currentUtilityTypeIds, value, unchecked);
      const newParam = (
        newVal != null
          ? newVal.map((v) => findOrBuildOption(v, utilityTypes))
          : []
      ) as MultiSelectControlledOption[];
      return { newParam, defaultObjectUpdate: { utilityTypeIds: newVal } };
    },
  });

  const sitesSetSelectedValues = createSetSelectedValues(
    LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_SITE_IDS,
    setCurrentSiteIds,
    (param) => ({
      siteIds:
        param.length > 0
          ? param.map((o) => o.value)
          : sites.map((site: any) => site.value),
    }),
  );

  const sitesOnChange = createOnChangeFn({
    storageKey: LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_SITE_IDS,
    setState: setCurrentSiteIds,
    run: (value, unchecked) => {
      const newIds =
        updateArr(currentSiteIds, value, unchecked) ??
        sites.map((site: any) => site.value);
      const opts = !areSitesFiltered ? sites : getSitesFiltered();
      const newParam = (Array.isArray(newIds) ? newIds : []).map((id) =>
        findOrBuildOption(id, opts),
      ) as MultiSelectControlledOption[];
      return { newParam, defaultObjectUpdate: { siteIds: newIds } };
    },
  });

  const businessUnitSetSelectedValues = createSetSelectedValues(
    LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_BUSINESS_UNIT,
    setCurrentBusinessUnits,
    (param) => ({
      businessUnits: param.length > 0 ? param.map((o) => o.value) : null,
    }),
  );

  const businessUnitOnChange = createOnChangeFn({
    storageKey: LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_BUSINESS_UNIT,
    setState: setCurrentBusinessUnits,
    run: (value, unchecked) => {
      const newVal = updateArr(currentBusinessUnits, value, unchecked);
      const newParam = (
        newVal != null
          ? newVal.map((v) => findOrBuildOption(v, businessUnits))
          : []
      ) as MultiSelectControlledOption[];
      return { newParam, defaultObjectUpdate: { businessUnits: newVal } };
    },
  });

  const regionSetSelectedValues = createSetSelectedValues(
    LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COUNTRY_REGION,
    setCurrentCountryRegions,
    (param) => ({
      countryRegions: param.length > 0 ? param.map((o) => o.value) : null,
    }),
  );

  const regionOnChange = createOnChangeFn({
    storageKey: LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_COUNTRY_REGION,
    setState: setCurrentCountryRegions,
    run: (value, unchecked) => {
      const newVal = updateArr(currentCountryRegions, value, unchecked);
      const newParam = (
        newVal != null
          ? newVal.map((v) => findOrBuildOption(v, countryRegions))
          : []
      ) as MultiSelectControlledOption[];
      return { newParam, defaultObjectUpdate: { countryRegions: newVal } };
    },
  });

  const statusSetSelectedValues = createSetSelectedValues(
    LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_STATUS,
    setCurrentStatus,
    () => ({}),
    { setSearchActive: false },
  );

  const statusOnChange = createOnChangeFn({
    storageKey: LOCAL_STORAGE_KEYS.MISSING_DOCUMENTS_PAGE_STATUS,
    setState: setCurrentStatus,
    updateStateAndStorage: false,
    run: (value, unchecked) => {
      const newVal = updateArr(currentStatus, value, unchecked);
      const newParam = (Array.isArray(newVal) ? newVal : []).map((v) =>
        findOrBuildOption(v, allStatusOptions),
      ) as MultiSelectControlledOption[];
      return { newParam, defaultObjectUpdate: { collectorStatuses: newVal } };
    },
  });

  const resetFilterState = () => {
    const resetFY = calculateCurrentFiscalYear(companyMonths);
    setCurrentFY(resetFY);
    setCurrentCountries([]);
    setCurrentSiteIds([]);
    setCurrentUtilityTypeIds([]);
    setCurrentBusinessUnits([]);
    setCurrentCountryRegions([]);
    setCurrentStatus(getStatusOptions(INITIAL_STATUS_OPTIONS));
    setAreSitesFiltered(false);
    setOnlyMissingState(false);

    defaultObject.current = {
      ...defaultObject.current,
      siteIds: sites.map((site: any) => site.value),
      utilityTypeIds: null,
      fromDate: getFiscalYear(companyMonths, resetFY).fromDate,
      toDate: getFiscalYear(companyMonths, resetFY).endDate,
      businessUnits: null,
      countryRegions: null,
      countries: null,
      onlyMissing: false,
      collectorStatuses: INITIAL_STATUS_OPTIONS.map((s) => s.key),
    };

    clearStoredFilters(resetFY);
  };

  return {
    defaultObject,
    currentFY,
    setCurrentFY,
    onlyMissing,
    setOnlyMissing,
    searchActive,
    setSearchActive,
    enableSearch,
    areSitesFiltered,
    currentCountries,
    currentSiteIds,
    currentUtilityTypeIds,
    currentBusinessUnits,
    currentCountryRegions,
    currentStatus,
    getSitesFiltered,
    createSetSelectedValues,
    createOnChangeFn,
    updateArr,
    resetFilterState,
    allStatusOptions,
    setCurrentCountries,
    setCurrentSiteIds,
    setCurrentUtilityTypeIds,
    setCurrentBusinessUnits,
    setCurrentCountryRegions,
    setCurrentStatus,
    countrySetSelectedValues,
    countryOnChange,
    utilityTypeSetSelectedValues,
    utilityTypeOnChange,
    sitesSetSelectedValues,
    sitesOnChange,
    businessUnitSetSelectedValues,
    businessUnitOnChange,
    regionSetSelectedValues,
    regionOnChange,
    statusSetSelectedValues,
    statusOnChange,
  };
};
